import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BreadcrumbProvider } from './breadcrumb-context';
import { msalConfig, loginRequest } from './config/msal.config';
import { createAppRouter } from './config/routes.config';
import { queryClient } from './shared/api/queryClient';
import { setupApiInterceptors } from './shared/api/apiClient';
import "./index.css";

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance.initialize().then(() => {
  // Default to using the first account if no account is active on page load
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // Listen for sign-in event and set active account
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  // Setup API interceptors for authentication
  setupApiInterceptors(msalInstance);
});

/**
 * Main App Component
 * Handles authentication initialization and router setup
 */
function App() {
  const { instance, accounts } = useMsal();
  const [idTokenClaims, setIdTokenClaims] = useState(null);
  const [router, setRouter] = useState(null);
  
  useEffect(() => {
    if (accounts.length === 0) {
      // If there's no account, initiate login process
      instance.loginPopup(loginRequest)
        .then((response) => {
          instance.setActiveAccount(response.account);
        })
        .catch((error) => {
          console.error('Login error:', error);
        });
    } else {
      const activeAccount = instance.getActiveAccount();
      if (activeAccount) {
        setIdTokenClaims(activeAccount.idTokenClaims);

        // Get user roles/permissions
        const request = {
          scopes: [import.meta.env.VITE_APP_ID_URI + "/.default"],
          account: activeAccount
        };
  
        instance.acquireTokenSilent(request)
          .then(response => {
            // Token acquired successfully
            // Roles are available in response.idTokenClaims.roles
          })
          .catch(error => {
            console.error('Token acquisition error:', error);
          });
      }
    }
  }, [instance, accounts]);

  // Create router with current idTokenClaims
  useEffect(() => {
    setRouter(createAppRouter(idTokenClaims));
  }, [idTokenClaims]);

  if (!router) {
    return null; // Or a loading spinner
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BreadcrumbProvider>
        <RouterProvider router={router} />
      </BreadcrumbProvider>
      {/* React Query Devtools - only in development */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

// Render the application
ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>
  </React.StrictMode>
);
